// src/mainframes/sidebar/NotificationFormModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import { Play } from "lucide-react";

import {
  useGetNotificationMetaQuery,
  useCreateNotificationSettingMutation,
  useUpdateNotificationSettingMutation,
} from "../../redux/admin/notificationApi";

// ✅ use your mp3 registry (the one you shared)
import { SOUND_OPTIONS } from "../../assets/soundMap";
import { previewSound } from "../../assets/play";

const ALARM_TYPES = [
  { key: "acc_on", label: "Ignition On" },
  { key: "acc_off", label: "Ignition Off" },
  { key: "overspeed_alarm", label: "Overspeed" },
  { key: "device_timeout", label: "Device Offline" },
  { key: "device_connected", label: "Device Connected" },
  { key: "device_disconnected", label: "Device Disconnected" },
  { key: "geofence_entry", label: "Geofence Entry" },
  { key: "geofence_exit", label: "Geofence Exit" },
  { key: "low_battery_alarm", label: "Low Battery" },
  { key: "power_off_alarm", label: "Power Off" },
  { key: "power_restored", label: "Power Restored" },
  { key: "low_speed_alarm", label: "Idle Alert" },
  { key: "harsh_acceleration", label: "Harsh Acceleration" },
  { key: "harsh_braking", label: "Harsh Braking" },
  { key: "harsh_cornering", label: "Harsh Cornering" },
  { key: "sos_alarm", label: "SOS Alarm" },
  { key: "vibration_alarm", label: "Vibration Alarm" },
  { key: "towing_alarm", label: "Towing Alarm" },
  { key: "unauthorized_movement", label: "Unauthorized Movement" },
  { key: "collision_alarm", label: "Collision Alarm" },
  { key: "obd_error", label: "OBD Error" },
  { key: "low_tire_pressure", label: "Low Tire Pressure" },
];

const DEFAULT_FORM = {
  alarmType: "",
  channels: ["socket", "push", "web"],
  sound: "default",
  priority: "medium",
  allDevices: true,
  deviceId: null, // ✅ keep for future (if you enable device-specific)
  isActive: true,
};

export default function NotificationFormModal({
  open,
  onClose,
  editData,
  presetDevice = null,
  titleOverride = "",
}) {
  const { data: meta } = useGetNotificationMetaQuery();
  const [createSetting, { isLoading: creating }] =
    useCreateNotificationSettingMutation();
  const [updateSetting, { isLoading: updating }] =
    useUpdateNotificationSettingMutation();

  const [form, setForm] = useState(DEFAULT_FORM);

  // ✅ combine backend channels (if provided) with safe fallback
  const channelsList = useMemo(() => {
    const list = meta?.channels?.length ? meta.channels : ["socket", "web", "mail", "push", "telegram"];
    // keep unique
    return Array.from(new Set(list));
  }, [meta]);

  // ✅ on open/edit change: hydrate form
  useEffect(() => {
    if (!open) return;

    if (editData?._id) {
      setForm({
        ...DEFAULT_FORM,
        ...editData,
        // ensure array defaults
        channels: Array.isArray(editData.channels) && editData.channels.length ? editData.channels : DEFAULT_FORM.channels,
        sound: editData.sound || "default",
        priority: editData.priority || "medium",
        allDevices: typeof editData.allDevices === "boolean" ? editData.allDevices : true,
        deviceId: editData.deviceId ?? null,
        isActive: typeof editData.isActive === "boolean" ? editData.isActive : true,
      });
    } else {
      setForm({
        ...DEFAULT_FORM,
        allDevices: presetDevice ? false : DEFAULT_FORM.allDevices,
        deviceId: presetDevice?._id || null,
      });
    }
  }, [open, editData, presetDevice]);

  const isBusy = creating || updating;

  const handleClose = () => {
    if (isBusy) return;
    onClose?.();
  };

  const toggleChannel = (ch, checked) => {
    setForm((prev) => {
      const set = new Set(prev.channels || []);
      if (checked) set.add(ch);
      else set.delete(ch);
      const next = Array.from(set);

      // ✅ keep at least one channel
      if (next.length === 0) return prev;

      return { ...prev, channels: next };
    });
  };

  const handleSave = async () => {
    if (!form.alarmType) return;

    const payload = {
      alarmType: form.alarmType,
      channels: form.channels,
      sound: form.sound,
      priority: form.priority,
      allDevices: !!form.allDevices,
      deviceId: form.allDevices ? null : form.deviceId || null,
      isActive: form.isActive !== false,
    };

    try {
      if (editData?._id) {
        await updateSetting({ id: editData._id, ...payload }).unwrap();
      } else {
        await createSetting(payload).unwrap();
      }
      handleClose();
    } catch (e) {
      // optional: show toast/snackbar (not crashing UI)
      console.error("Notification save failed:", e);
    }
  };

  const title = titleOverride || (editData?._id ? "Edit Notification" : "Add Notification");

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>

      <DialogContent>
        <TextField
          select
          fullWidth
          margin="normal"
          label="Type"
          value={form.alarmType}
          onChange={(e) => setForm({ ...form, alarmType: e.target.value })}
        >
          {ALARM_TYPES.map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

        {presetDevice ? (
          <Typography sx={{ mt: 1, color: "text.secondary", fontWeight: 700 }}>
            Device scope: {presetDevice.name || presetDevice.imei || "Selected device"}
          </Typography>
        ) : null}

        {/* 🔊 Sound + Preview */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
          <TextField
            select
            fullWidth
            label="Sound"
            value={form.sound}
            onChange={(e) => setForm({ ...form, sound: e.target.value })}
          >
            {SOUND_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <IconButton
            sx={{ mt: 0.2 }}
            disabled={!form.sound || form.sound === "mute"}
            onClick={() => previewSound(form.sound)}
          >
            <Play size={18} />
          </IconButton>
        </Box>

        <TextField
          select
          fullWidth
          margin="normal"
          label="Priority"
          value={form.priority}
          onChange={(e) => setForm({ ...form, priority: e.target.value })}
        >
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
        </TextField>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Channels</Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {channelsList.map((ch) => (
            <FormControlLabel
              key={ch}
              sx={{ mr: 2 }}
              control={
                <Checkbox
                  checked={(form.channels || []).includes(ch)}
                  onChange={(e) => toggleChannel(ch, e.target.checked)}
                />
              }
              label={ch}
            />
          ))}
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={handleClose} color="inherit" disabled={isBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isBusy || !form.alarmType}
          >
            {isBusy ? "Saving..." : "Save"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
